/** Consumer mail domains accepted at registration (TR + common intl). */
export const ALLOWED_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.com.tr',
  'outlook.com',
  'outlook.com.tr',
  'live.com',
  'live.com.tr',
  'msn.com',
  'yahoo.com',
  'yahoo.com.tr',
  'ymail.com',
  'mynet.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'yandex.com',
  'yandex.ru',
  'mail.ru',
  'inbox.ru',
  'list.ru',
  'bk.ru',
  'gmx.com',
  'gmx.net',
  'aol.com',
  'windowslive.com',
  'zoho.com',
  'mail.com',
  'tutanota.com',
  'tuta.com',
  'fastmail.com',
  'yandex.com.tr',
  'ya.ru',
  'yandex.ua',
  'yandex.kz',
  'yandex.by',
  'hotmail.co.uk',
  'outlook.co.uk',
  'live.co.uk',
  'passport.com',
];

const ALLOWED_SET = new Set(ALLOWED_EMAIL_DOMAINS);

/** Common typos → suggested domain */
export const EMAIL_DOMAIN_TYPO_HINTS = {
  'gmil.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.om': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outllok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlook.con': 'outlook.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'mynet.co': 'mynet.com',
  'mynet.con': 'mynet.com',
  'mynet.com.tr': 'mynet.com',
  'yandex.co': 'yandex.com',
  'yandex.con': 'yandex.com',
  'yandx.com': 'yandex.com',
  'yande.com': 'yandex.com',
  'yanex.com': 'yandex.com',
  'icloud.co': 'icloud.com',
  'icloud.con': 'icloud.com',
  'icoud.com': 'icloud.com',
  'icloude.com': 'icloud.com',
  'windowslive.co': 'windowslive.com',
  'windows.live.com': 'live.com',
  'live.con': 'live.com',
  'live.co': 'live.com',
};

const BASIC_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmailAddress(email) {
  return String(email || '').trim().toLowerCase();
}

export function getEmailDomain(email) {
  const value = normalizeEmailAddress(email);
  const at = value.lastIndexOf('@');
  if (at < 1 || at === value.length - 1) return '';
  return value.slice(at + 1);
}

export function isBasicEmailFormat(email) {
  return BASIC_EMAIL_RE.test(normalizeEmailAddress(email));
}

export function isAllowedEmailDomain(email) {
  const domain = getEmailDomain(email);
  return Boolean(domain && ALLOWED_SET.has(domain));
}

export function suggestEmailDomain(email) {
  const domain = getEmailDomain(email);
  if (!domain) return null;
  if (ALLOWED_SET.has(domain)) return null;
  if (EMAIL_DOMAIN_TYPO_HINTS[domain]) return EMAIL_DOMAIN_TYPO_HINTS[domain];
  // distance-1 match against allowlist (e.g. gmail.vom)
  for (const allowed of ALLOWED_EMAIL_DOMAINS) {
    if (editDistanceAtMost1(domain, allowed)) return allowed;
  }
  return null;
}

function editDistanceAtMost1(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (la === lb) {
    let diff = 0;
    for (let i = 0; i < la; i += 1) {
      if (a[i] !== b[i]) {
        diff += 1;
        if (diff > 1) return false;
      }
    }
    return diff === 1;
  }
  const shorter = la < lb ? a : b;
  const longer = la < lb ? b : a;
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) {
      i += 1;
      j += 1;
      continue;
    }
    if (skipped) return false;
    skipped = true;
    j += 1;
  }
  return true;
}

/**
 * @returns {null|string} error message key fragment or null if ok
 * Caller maps to i18n / API message.
 */
export function validateRegisterEmail(email, { required = false } = {}) {
  const value = normalizeEmailAddress(email);
  if (!value) {
    return required ? 'required' : null;
  }
  if (!isBasicEmailFormat(value)) return 'invalid';
  if (value.endsWith('@vurgunbet.local')) return 'invalid';
  if (!isAllowedEmailDomain(value)) return 'domain';
  return null;
}
